import React from "react";
import { CreateFunction, UpdateFunction } from "@hex-insights/app-modules";
import { Button, ClassNameProps, DistributiveOmit, getObjectValues, Modal, Row, StyleProps } from "@hex-insights/core";
import { Form, FormEditButtonPair, FormState, settings, SubmitButton, useInternalField } from "@hex-insights/forms";
import {
	PostCreateMutation,
	PostDetailQuery,
	PostUpdateMutation,
	useInternalPortalsViewerInfoQuery,
	useUserGroupNamesAndColorsQuery,
} from "../../../Utilities";
import { AuthorTimestampView, ModalPost, PostModal } from "../../Components/Post";
import { PostFormState, PostFormValues } from "../../FormUtilities";
import { UserUtilities } from "../../Utilities";
import { MediaAttachmentCustomForm } from "../MediaAttachment";
import { BaseFormProps } from "../shared";
import * as Field from "./Field";
import {
	useCreateFormControl,
	useCreateFormSubmission,
	useDetailFormControl,
	useDetailFormSubmission,
	useReadOnlyForm,
} from "./hooks";

export {
	type UseCreateFormArgs,
	useCreateForm,
	type UseDetailFormArgs,
	useDetailForm,
	type UseReadOnlyFormArgs,
	useReadOnlyForm,
} from "./hooks";

export type ControlledCreateProps = Omit<CreateProps<PostCreateMutation["createPost"]>, "formState" | "create"> & {
	formStateArgs?: PostFormState.UseCreateFormStateArgs;
};

/**
 * Create form of the Post model using an internally managed form state.
 */
export function ControlledCreate({ formStateArgs, ...props }: ControlledCreateProps) {
	const { formState, create } = useCreateFormControl(formStateArgs);

	return <Create formState={formState} create={create} {...props} />;
}

export type CreateProps<T> = {
	formState: FormState<PostFormValues.Create>;
	create: CreateFunction<PostFormValues.Create, T>;
	onSuccess: (formState: FormState<PostFormValues.Create>, data: T) => void;
	formNameSuffix?: string;
	columnClassName?: string;
	columnStyle?: React.CSSProperties;
} & BaseFormProps<PostFormValues.Create> &
	Partial<ClassNameProps & StyleProps>;

/**
 * Create form of the Post model using the given form state.
 */
export function Create<T>({
	formState,
	create,
	onSuccess,
	formNameSuffix,
	columnClassName,
	columnStyle,
	...props
}: CreateProps<T>) {
	const { onSubmit } = useCreateFormSubmission({ formState, create, onSuccess });

	const formName = "post.create" + (formNameSuffix ? "." + formNameSuffix : "");

	const { formValues } = formState;
	const { data } = useInternalPortalsViewerInfoQuery();
	const viewer = data?.viewer;
	const { data: userGroupsData } = useUserGroupNamesAndColorsQuery({
		variables: {
			filters: { idIn: formValues.sharingPolicy[0].userGroupIDs },
		},
		skip: formValues.sharingPolicy[0].userGroupIDs.length === 0,
	});
	const previewPost = React.useMemo<ModalPost>(
		() => ({
			...formValues,
			id: "preview",
			createdAt: new Date().toISOString(),
			author: viewer ?? UserUtilities.placeholderCurrentUser,
			mediaAttachments: formValues.mediaAttachments.map((e, i) => ({
				...e,
				id: `preview-${i}`,
				fileURL: e.fileKey ? settings.uploads.fileInfoToURL(e.fileKey, e.fileContentType) : "",
			})),
			sharingPolicy: {
				id: "preview",
				isPublic: formValues.sharingPolicy[0].isPublic,
				userGroups: userGroupsData?.userGroupConnection.edges.map((e) => e.node) ?? [],
			},
			socialComments: [],
			socialReactions: [],
		}),
		[viewer, formValues, userGroupsData],
	);

	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	return (
		<React.Fragment>
			<Form name={formName} formState={formState} onSubmit={formState.onSubmitWrapper(onSubmit)} {...props}>
				<Form.Column className={columnClassName} style={columnStyle}>
					<AuthorTimestampView post={previewPost} />

					<CreateFields formState={formState} />

					<Row justify="spaced-end">
						<Button variant="secondary" onClick={openModal}>
							Preview
						</Button>
						<SubmitButton
							submissionStatus={formState.submissionStatus}
							onClick={formState.onSubmitWrapper(onSubmit)}
							disabled={getObjectValues(formState.submissionLocks).some((e) => e)}
						>
							Publish
						</SubmitButton>
					</Row>
				</Form.Column>
			</Form>

			<Modal.If condition={isModalOpen}>
				<PostModal
					post={previewPost}
					noReactions
					noComments
					isPreview
					withBackButton
					backButtonText="Back to Form"
					onClose={closeModal}
				/>
			</Modal.If>
		</React.Fragment>
	);
}

type CreateFieldsProps = {
	formState: FormState<PostFormValues.Create>;
};

function CreateFields({ formState }: CreateFieldsProps) {
	return (
		<React.Fragment>
			<Field.Title formState={formState} style={{ width: "100%" }} />
			<Field.Body formState={formState} noHeader placeholder="Write your post..." style={{ width: "100%" }} />
			<Field.MediaAttachments.SubForm formState={formState} style={{ width: "100%" }} />
			<Field.SharingPolicy.SubForm formState={formState} />
		</React.Fragment>
	);
}

export type ControlledDetailProps = Omit<DetailProps<PostUpdateMutation["updatePost"]>, "formState" | "update"> & {
	formStateArgs?: Partial<DistributiveOmit<PostFormState.UseDetailFormStateArgs, "initialFormValues">>;
};

/**
 * Detail form of the Post model using an internally managed form state.
 */
export function ControlledDetail({ formStateArgs, ...props }: ControlledDetailProps) {
	const { formState, update } = useDetailFormControl({ ...formStateArgs, post: props.post });

	return <Detail formState={formState} update={update} {...props} />;
}

export type DetailProps<T> = {
	post: PostDetailQuery["post"];
	formState: FormState<PostFormValues.Detail>;
	update: UpdateFunction<PostFormValues.Detail, T>;
	onSuccess: (formState: FormState<PostFormValues.Detail>, data: T) => void;
	formNameSuffix?: string;
} & BaseFormProps<PostFormValues.Detail> &
	Partial<ClassNameProps & StyleProps>;

/**
 * Detail form of the Post model using the given form state.
 */
export function Detail<T>({ formState, post, update, onSuccess, formNameSuffix, ...props }: DetailProps<T>) {
	const { onSubmit } = useDetailFormSubmission({ formState, update, onSuccess });

	const formName = `post.detail.${post.id}` + (formNameSuffix ? "." + formNameSuffix : "");

	return (
		<Form name={formName} formState={formState} onSubmit={formState.onSubmitWrapper(onSubmit)} {...props}>
			<Form.Column style={{ width: "100%" }}>
				<AuthorTimestampView post={post} />

				<FormEditButtonPair formState={formState} />

				<DetailFields formState={formState} post={post} />
			</Form.Column>
		</Form>
	);
}

export type ControlledReadOnlyProps = Omit<ReadOnlyProps, "formState"> & {
	formStateArgs?: Partial<DistributiveOmit<PostFormState.UseReadOnlyFormStateArgs, "initialFormValues">>;
};

/**
 * Read-only detail form of the Post model using an internally managed form state.
 */
export function ControlledReadOnly({ formStateArgs, ...props }: ControlledReadOnlyProps) {
	const { formState } = useReadOnlyForm({ ...formStateArgs, post: props.post });

	return <ReadOnly formState={formState} {...props} />;
}

export type ReadOnlyProps = {
	post: PostDetailQuery["post"];
	formState: FormState<PostFormValues.Detail>;
} & Partial<ClassNameProps & StyleProps>;

/**
 * Read-only detail form of the Post model using the given form state.
 */
export function ReadOnly({ formState, post, className, style }: ReadOnlyProps) {
	return (
		<div className={className} style={style}>
			<Form.Column>
				<DetailFields formState={formState} post={post} />
			</Form.Column>
		</div>
	);
}

type DetailFieldsProps = {
	post: PostDetailQuery["post"];
	formState: FormState<PostFormValues.Detail>;
};

function DetailFields({ formState, post }: DetailFieldsProps) {
	useInternalField(formState, "createdAt");
	useInternalField(formState, "authorID");

	return (
		<React.Fragment>
			<Field.Title formState={formState} style={{ width: "100%" }} />
			<Field.Body formState={formState} noHeader placeholder="Write your post..." style={{ width: "100%" }} />
			<Field.SharingPolicy.Detail sharingPolicy={post.sharingPolicy} />
			<MediaAttachmentCustomForm.ControlledMultiEditForm postID={post.id} mediaAttachments={post.mediaAttachments} />
		</React.Fragment>
	);
}
